import React, { useMemo, useState } from 'react';

import { EditableEvent } from 'types/event.types';
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle
} from '@epoch-ui/components/Modal/modal';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { formatDateInTimezone, guessTimezone } from 'utils/dateTimeUtils';
import {
  COMMUNICATION_REMINDER_MEDIUM,
  EPOCH_COMMUNICATION_NOTIFICATION,
  EPOCH_COMMUNICATION_TYPE
} from '@event-editor/tabs/EditEventCommunications/types/communication.types';
import { Button } from '@epoch-ui/components/Button';
import { SlackPreview, SlackButton } from '@epoch-components/SlackPreview';
import { EventEmailPreview } from '@epoch-components/EmailPreview';

interface CommunicationPreviewModalProps extends React.ComponentPropsWithoutRef<typeof Modal> {
  communication: EPOCH_COMMUNICATION_NOTIFICATION;
}

type CommunicationPreviewData = {
  event: EditableEvent;
  communication: EPOCH_COMMUNICATION_NOTIFICATION;
};

/**
 * A unified preview modal that can show both Slack and Email previews for various notification types.
 * TODO: Customize buttons and content based on notification type.
 * - We could also refactor this with 'EmailPreviewReminderModal' and 'SlackPreviewReminderModal' components.
 */
export const CommunicationPreviewModal: React.FC<CommunicationPreviewModalProps> = ({
  communication,
  ...modalProps
}) => {
  const event = useEditableEvent();
  const { title: communicationTitle, mediums } = communication;

  const hasSlack = mediums.includes(COMMUNICATION_REMINDER_MEDIUM.SLACK);
  const hasEmail = mediums.includes(COMMUNICATION_REMINDER_MEDIUM.EMAIL);
  const hasBoth = hasSlack && hasEmail;

  const [activePreview, setActivePreview] = useState<string>(
    hasSlack ? COMMUNICATION_REMINDER_MEDIUM.SLACK : COMMUNICATION_REMINDER_MEDIUM.EMAIL
  );

  return (
    <Modal {...modalProps}>
      <ModalContent className="tw-max-w-[688px]">
        <ModalHeader>
          <ModalTitle>{communicationTitle} </ModalTitle>
        </ModalHeader>
        <ModalDescription className="tw-sr-only">{communicationTitle} Preview</ModalDescription>

        {activePreview === COMMUNICATION_REMINDER_MEDIUM.SLACK ? (
          <SlackPreviewContent event={event} communication={communication} />
        ) : (
          <EmailPreviewContent event={event} communication={communication} />
        )}

        {hasBoth && (
          <div className="tw-mb-4 tw-flex tw-justify-end">
            <Button
              variant="link"
              size="sm"
              onClick={() =>
                setActivePreview(
                  activePreview === COMMUNICATION_REMINDER_MEDIUM.SLACK
                    ? COMMUNICATION_REMINDER_MEDIUM.EMAIL
                    : COMMUNICATION_REMINDER_MEDIUM.SLACK
                )
              }
            >
              See {activePreview === COMMUNICATION_REMINDER_MEDIUM.SLACK ? 'email' : 'slack'}{' '}
              preview
            </Button>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

// Email Preview Component
const EmailPreviewContent: React.FC<CommunicationPreviewData> = ({ communication, event }) => {
  const { message } = communication;

  const subjectLine = useMemo(() => {
    switch (communication.type) {
      case EPOCH_COMMUNICATION_TYPE.EVENT_QUESTIONNAIRE:
        return `Complete your registration for ${event.title}`;
      case EPOCH_COMMUNICATION_TYPE.SELF_CHECK_IN:
        return `Check in for ${event.title}`;
      case EPOCH_COMMUNICATION_TYPE.FEEDBACK:
      case EPOCH_COMMUNICATION_TYPE.RESEND_FEEDBACK:
        return `How was ${event.title}?`;
      default:
        return '';
    }
  }, [communication.type, event.title]);

  return (
    <div>
      <p className="tw-mb-4 tw-text-sm tw-leading-relaxed tw-text-primary-grey">
        Email Subject line: {subjectLine}
      </p>
      <EventEmailPreview event={event} message={message} />
    </div>
  );
};

// Slack Preview Component
const SlackPreviewContent: React.FC<CommunicationPreviewData> = ({ communication, event }) => {
  const currentTime = formatDateInTimezone(
    new Date(),
    event.timezoneCode || guessTimezone(),
    'H:mm A'
  );

  const messageContent = useMemo(() => {
    switch (communication.type) {
      case EPOCH_COMMUNICATION_TYPE.EVENT_QUESTIONNAIRE:
        return 'Fill out this questionnaire to complete your registration for this event.';
      case EPOCH_COMMUNICATION_TYPE.SELF_CHECK_IN:
        return 'Please check in now if you’re attending this event.';
      case EPOCH_COMMUNICATION_TYPE.FEEDBACK:
      case EPOCH_COMMUNICATION_TYPE.RESEND_FEEDBACK:
        return `Thanks for attending this event! Please share feedback on your experience.\n\n${
          event.isFeedbackAnonymous
            ? 'Your responses will be anonymous.'
            : 'Your name will be shared with your responses.'
        }`;
      default:
        return '';
    }
  }, [communication.type, event.isFeedbackAnonymous]);

  const actions = useMemo(() => {
    switch (communication.type) {
      case EPOCH_COMMUNICATION_TYPE.EVENT_QUESTIONNAIRE:
        return (
          <>
            <SlackButton variant="primary">Submit questionnaire</SlackButton>
            <SlackButton variant="danger">Not going</SlackButton>
          </>
        );
      case EPOCH_COMMUNICATION_TYPE.SELF_CHECK_IN:
        return <SlackButton variant="primary">Check in</SlackButton>;
      case EPOCH_COMMUNICATION_TYPE.FEEDBACK:
      case EPOCH_COMMUNICATION_TYPE.RESEND_FEEDBACK:
        return <SlackButton variant="primary">Submit feedback</SlackButton>;
      default:
        return (
          <>
            <SlackButton variant="primary">Going</SlackButton>
            <SlackButton variant="default">Maybe</SlackButton>
            <SlackButton variant="danger">Not going</SlackButton>
          </>
        );
    }
  }, [communication.type]);

  return (
    <SlackPreview timestamp={currentTime}>
      <SlackPreview.Message markdownContent={messageContent} />
      <SlackPreview.Divider />
      <SlackPreview.EventDetails event={event} />
      <SlackPreview.Actions>{actions}</SlackPreview.Actions>
    </SlackPreview>
  );
};
